import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { AUTH_ENABLED } from '../authConfig';
import { useTier } from '../useTier';
import NavigationBar from './NavigationBar';
import UpgradeScreen from './UpgradeScreen';

/**
 * AUTH-7 — page-level tier gate for the paid routes: /history, /msa, /compare
 * (ADR-007 boundary — paid = persisted history + MSA/GRR + validation evidence).
 * The interim passcode route walls (MSAGate / PremiumAccess.PasscodeGate) were
 * retired at the Phase C cutover (§7c) — TierGate is now the only route gate.
 *
 * Flag-bound at module load, exactly like useTier / apiFetch / AuthProvider:
 *   - AUTH off (rollback) -> TierGateDisabled: a strict pass-through (renders
 *     children) — since the passcode wall is gone, a flags-off rollback means
 *     these routes are open, not locked (the accepted §7c trade).
 *   - AUTH on   -> TierGateEnabled: loading spinner while Clerk resolves, then the
 *     page for an entitled user, or the UpgradeScreen (AUTH-13 "not-entitled") for
 *     a free user / anonymous visitor.
 *
 * UI-only gate: requireTier() on the server is the real boundary (a free user who
 * bypasses this still gets 401/402 from the API).
 */

function TierLoading() {
    return (
        <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#90caf9' }} />
        </Box>
    );
}

function TierGateEnabled({ minTier = 'paid', feature, children }) {
    const { isLoaded, isAtLeast, isSignedIn, tier, userEmail } = useTier();

    if (!isLoaded) {
        // Avoid flashing the upsell before Clerk resolves the session/tier.
        return (<><NavigationBar /><TierLoading /></>);
    }

    // TIER-3 Phase 0 (design doc §6.1) refactor: isAtLeast(minTier) replaces the
    // old `minTier === 'admin' ? isAdmin : isPaid` two-case formula. Identical
    // output for minTier='paid' (default) and 'admin'; also correct for a future
    // minTier='enterprise' route with no further change here.
    const entitled = isAtLeast(minTier);
    if (entitled) return children;

    return (
        <>
            <NavigationBar />
            <UpgradeScreen feature={feature} minTier={minTier} isSignedIn={isSignedIn} currentTier={tier} userEmail={userEmail} />
        </>
    );
}

function TierGateDisabled({ children }) {
    return children; // AUTH off (rollback): no-op pass-through — no passcode gate exists anymore.
}

export const TierGate = AUTH_ENABLED ? TierGateEnabled : TierGateDisabled;

export default TierGate;
